/**
 * The org Templates library table. A focused mirror of `DocumentList` — same
 * TanStack `useReactTable` + shadcn `<Table>` anatomy, without the library's
 * drag-and-drop / multi-select machinery that templates don't need. Each row
 * carries `data-testid="template-row-{id}"` and `data-status` (latest-wins
 * compile status) so callers and tests can key off the row directly.
 */
import { type MouseEvent, type ReactNode, useMemo } from "react";
import {
    flexRender,
    getCoreRowModel,
    type OnChangeFn,
    type PaginationState,
    useReactTable,
} from "@tanstack/react-table";

import type { OutputTemplateListItem } from "@/api";
import { DataTablePagination } from "@/components/shared/DataTablePagination";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { getTemplateListColumns } from "./templateListColumns";
import { displayStatus } from "./useOutputTemplates";

interface TemplateListProps {
    templates: OutputTemplateListItem[];
    onRetry: (id: number) => void;
    onRowClick?: (id: number) => void;
    pagination: PaginationState;
    onPaginationChange: OnChangeFn<PaginationState>;
    rowCount: number;
    emptyState?: ReactNode;
}

export function TemplateList({
    templates,
    onRetry,
    onRowClick,
    pagination,
    onPaginationChange,
    rowCount,
    emptyState,
}: TemplateListProps) {
    const columns = useMemo(
        () => getTemplateListColumns({ onRetry }),
        [onRetry]
    );

    const table = useReactTable({
        data: templates,
        columns,
        rowCount,
        getRowId: (t) => String(t.id),
        state: { pagination },
        onPaginationChange,
        manualPagination: true,
        getCoreRowModel: getCoreRowModel(),
    });

    const handleRowClick = (
        event: MouseEvent<HTMLTableRowElement>,
        templateId: number
    ) => {
        if (
            !onRowClick ||
            !(event.target instanceof Node) ||
            !event.currentTarget.contains(event.target)
        ) {
            return;
        }
        onRowClick(templateId);
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div
                data-testid="template-list-container"
                className="overflow-hidden rounded-lg border"
            >
                {templates.length === 0 ? (
                    emptyState
                ) : (
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableHead
                                            key={header.id}
                                            className={
                                                header.column.columnDef.meta
                                                    ?.cellClassName
                                            }
                                        >
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext()
                                                  )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-testid={`template-row-${row.original.id}`}
                                    data-status={
                                        displayStatus(row.original) ?? "unknown"
                                    }
                                    className={cn(
                                        "group/row",
                                        onRowClick && "cursor-pointer"
                                    )}
                                    onClick={
                                        onRowClick
                                            ? (event) =>
                                                  handleRowClick(
                                                      event,
                                                      row.original.id
                                                  )
                                            : undefined
                                    }
                                >
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell
                                            key={cell.id}
                                            className={
                                                cell.column.columnDef.meta
                                                    ?.cellClassName
                                            }
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>
            <div className="py-2">
                <DataTablePagination table={table} entityLabel="templates" />
            </div>
        </div>
    );
}
